// ===========================================
// Roles — Page liste des rôles (refonte UX)
// ===========================================
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import {
    Table, Card, Input, Button, Tag, Grid, Popconfirm, Space, Tooltip, Empty,
} from 'antd';
import {
    PlusOutlined, EditOutlined, DeleteOutlined, SettingOutlined, SearchOutlined,
    SafetyCertificateOutlined, LockOutlined, UnlockOutlined, KeyOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { rolesService } from '../../common/services';
import { getRoleLabel } from '../../common/auth/roleLabels';

import MiniKpiCard from '../clients/components/MiniKpiCard';
import Can from '../../components/can';

import './roles.page.scss';

const Roles = () => {
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const navigate = useNavigate();
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.sm;

    const fetchRoles = async () => {
        setLoading(true);
        try {
            const res = await rolesService.getAll();
            setRoles(Array.isArray(res) ? res : res?.data || []);
        } catch (error) {
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchRoles(); }, []);

    const filteredRoles = useMemo(() => {
        if (!search) return roles;
        const q = search.toLowerCase();
        return roles.filter((r) =>
            `${r.name || ''} ${r.description || ''}`.toLowerCase().includes(q)
        );
    }, [roles, search]);

    const stats = useMemo(() => {
        const total = roles.length;
        const systeme = roles.filter((r) => r.isSystem === true).length;
        const personnalises = total - systeme;
        return { total, systeme, personnalises };
    }, [roles]);

    const handleDelete = async (id) => {
        try {
            await rolesService.delete(id);
            toast.success('Rôle supprimé');
            fetchRoles();
        } catch (error) {
            toast.error('Erreur lors de la suppression');
        }
    };

    const columns = [
        {
            title: 'Nom',
            dataIndex: 'name',
            key: 'name',
            width: 240,
            sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
            render: (t, r) => (
                <div className="role-cell">
                    <div className={`role-icon ${r.isSystem ? 'is-system' : 'is-custom'}`}>
                        {r.isSystem ? <LockOutlined /> : <UnlockOutlined />}
                    </div>
                    <div className="role-cell-body">
                        <span className="role-name">{getRoleLabel(t) || t || '—'}</span>
                        {r.isSystem && <Tag color="purple" className="role-system-tag">Système</Tag>}
                    </div>
                </div>
            ),
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            ellipsis: true,
            render: (v) => v || <span className="role-muted">—</span>,
        },
        {
            title: 'Permissions',
            dataIndex: 'permissions',
            key: 'permissions',
            width: 160,
            align: 'center',
            sorter: (a, b) => (a.permissions?.length || 0) - (b.permissions?.length || 0),
            render: (perms) => (
                <Tag icon={<KeyOutlined />} color="geekblue" className="role-perm-tag">
                    {(perms || []).length} permission{(perms || []).length > 1 ? 's' : ''}
                </Tag>
            ),
        },
        {
            title: 'Date création',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 140,
            sorter: (a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0),
            defaultSortOrder: 'descend',
            render: (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '—',
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 120,
            align: 'center',
            fixed: isMobile ? false : 'right',
            render: (_, record) => {
                const isSystemRole = record.isSystem === true;
                return (
                    <Space size={6}>
                        <Can perm="roles:update">
                            <Tooltip title={isSystemRole ? 'Rôle système : modification limitée aux permissions' : 'Modifier'}>
                                <Button
                                    size="small"
                                    icon={<EditOutlined />}
                                    className="role-action-btn role-action-edit"
                                    aria-label="Modifier ce rôle"
                                    onClick={() => navigate(`/dashboard/modifier-role/${record._id}`)}
                                />
                            </Tooltip>
                        </Can>
                        <Can perm="roles:delete">
                            {isSystemRole ? (
                                <Tooltip title="Rôle système non supprimable">
                                    <Button
                                        size="small"
                                        icon={<DeleteOutlined />}
                                        className="role-action-btn"
                                        disabled
                                        aria-label="Supprimer (désactivé)"
                                    />
                                </Tooltip>
                            ) : (
                                <Popconfirm
                                    title="Supprimer ce rôle ?"
                                    description="Vérifiez qu'aucun utilisateur ne l'utilise."
                                    onConfirm={() => handleDelete(record._id)}
                                    okText="Oui"
                                    cancelText="Non"
                                    okButtonProps={{ danger: true }}
                                >
                                    <Tooltip title="Supprimer">
                                        <Button
                                            size="small"
                                            icon={<DeleteOutlined />}
                                            className="role-action-btn role-action-delete"
                                            aria-label="Supprimer ce rôle"
                                        />
                                    </Tooltip>
                                </Popconfirm>
                            )}
                        </Can>
                    </Space>
                );
            },
        },
    ];

    return (
        <div className="roles-page">
            {/* Header */}
            <header className="roles-header">
                <div className="roles-header-text">
                    <h1 className="roles-title">
                        <SettingOutlined /> Rôles & permissions
                    </h1>
                    <p className="roles-subtitle">Définition des rôles et des autorisations d'accès</p>
                </div>
                <div className="roles-header-actions">
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => navigate('/dashboard/ajouter-role')}
                        className="roles-btn-add"
                    >
                        {!isMobile && 'Nouveau rôle'}
                    </Button>
                </div>
            </header>

            {/* KPIs */}
            <section className="roles-kpis" aria-label="Indicateurs rôles">
                <MiniKpiCard
                    icon={<SafetyCertificateOutlined />}
                    label="Total des rôles"
                    value={stats.total}
                    tone="blue"
                />
                <MiniKpiCard
                    icon={<LockOutlined />}
                    label="Rôles système"
                    value={stats.systeme}
                    tone="purple"
                />
                <MiniKpiCard
                    icon={<UnlockOutlined />}
                    label="Rôles personnalisés"
                    value={stats.personnalises}
                    tone="green"
                />
            </section>

            {/* Carte principale */}
            <Card className="roles-main-card">
                <div className="roles-toolbar">
                    <Input
                        placeholder="Rechercher un rôle (nom, description)…"
                        prefix={<SearchOutlined />}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        allowClear
                        className="roles-search"
                    />
                </div>

                <Table
                    dataSource={filteredRoles}
                    columns={columns}
                    rowKey="_id"
                    loading={{ spinning: loading, size: 'large', tip: 'Chargement…' }}
                    size="middle"
                    scroll={{ x: 'max-content' }}
                    locale={{
                        emptyText: loading ? <div style={{ minHeight: 220 }} /> : <Empty description="Aucun rôle" image={Empty.PRESENTED_IMAGE_SIMPLE} />,
                    }}
                    pagination={{
                        pageSize: 10,
                        showTotal: (t) => `${t} rôle${t > 1 ? 's' : ''}`,
                        showSizeChanger: true,
                    }}
                />
            </Card>
        </div>
    );
};

export default Roles;
